import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import LogRocket from 'logrocket';
import TagManager from 'react-gtm-module';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import createSagaMiddleware from 'redux-saga';
import JssProvider from 'react-jss/lib/JssProvider';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import { reducer as formReducer } from 'redux-form';
import {
  connectRouter,
  routerMiddleware,
  ConnectedRouter,
} from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { constructGTM } from './utilities/helpers';

import App from './App';
import './index.css';
import * as serviceWorker from './serviceWorker';
import { muiTheme, generateClassName } from './themes/theme';
import { LOG_ROCKET_APP_ID } from './constants';
import loginReducer from './store/reducers/login';
import modalReducer from './store/reducers/modal';
import userProfileReducer from './store/reducers/userProfile';
import notificationReducer from './store/reducers/notification';
import registrationReducer from './store/reducers/registration';
import skillsetReducer from './store/reducers/skillset';
import jobsReducer from './store/reducers/jobs';
import navigationReducer from './store/reducers/navigation';
import settingsReducer from './store/reducers/settings';
import builderApprovalReducer from './store/reducers/builderApproval';
import builderPicsReducer from './store/reducers/builderPics';
import {
  loginWatcher,
  signUpWatcher,
  getUserProfileWatcher,
  updateUserProfileWatcher,
  getSkillsetOptionsWatcher,
  getJobsWatcher,
  createJobWatcher,
  updateJobWatcher,
  navigationWatcher,
  updateNotificationSettingsWatcher,
  updateBusinessAttributesWatcher,
  confirmApproveBuilderWatcher,
} from './store/sagas';

const rootReducer = history =>
  combineReducers({
    router: connectRouter(history),
    login: loginReducer,
    form: formReducer,
    modal: modalReducer,
    registration: registrationReducer,
    notification: notificationReducer,
    userProfile: userProfileReducer,
    skillset: skillsetReducer,
    jobs: jobsReducer,
    navigation: navigationReducer,
    settings: settingsReducer,
    builderApproval: builderApprovalReducer,
    builderPics: builderPicsReducer,
  });

// create the saga middleware
const sagaMiddleware = createSagaMiddleware();

// dev tools middleware
const reduxDevTools =
  // eslint-disable-next-line no-underscore-dangle
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__();

export const history = createBrowserHistory();

let store;

if (reduxDevTools !== undefined) {
  store = createStore(
    rootReducer(history), // root reducer with router state
    compose(
      applyMiddleware(routerMiddleware(history), sagaMiddleware),
      reduxDevTools,
    ),
  );
} else {
  store = createStore(
    rootReducer(history),
    compose(applyMiddleware(routerMiddleware(history), sagaMiddleware)),
  );
}

// run the saga
sagaMiddleware.run(loginWatcher);
sagaMiddleware.run(signUpWatcher);
sagaMiddleware.run(getUserProfileWatcher);
sagaMiddleware.run(updateUserProfileWatcher);
sagaMiddleware.run(getSkillsetOptionsWatcher);
sagaMiddleware.run(getJobsWatcher);
sagaMiddleware.run(createJobWatcher);
sagaMiddleware.run(updateJobWatcher);
sagaMiddleware.run(navigationWatcher);
sagaMiddleware.run(updateNotificationSettingsWatcher);
sagaMiddleware.run(updateBusinessAttributesWatcher);
sagaMiddleware.run(confirmApproveBuilderWatcher);

LogRocket.init(LOG_ROCKET_APP_ID);

// GOOGLE TAG MANAGER
const tagManagerArgs = {};

TagManager.initialize(constructGTM(tagManagerArgs));

const app = (
  <JssProvider generateClassName={generateClassName}>
    <MuiThemeProvider theme={muiTheme}>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <CssBaseline />
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </Provider>
    </MuiThemeProvider>
  </JssProvider>
);

ReactDOM.render(app, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
